import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCard, MatCardContent, MatCardHeader } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { PackagePreviewRow } from 'app/_models/result';
import { PackageSelectorNamePipe } from 'app/_pipes/package-selector-name.pipe';
import { CalcConfigService } from '_services/calc-config.service';
import {
  buildPackagingCompare,
  formatDelta,
  ledgerColumns,
  metricChanged,
  metricDelta,
  methodologyNeedKey,
  PACKAGE_SIDES,
  PackageCompare,
  PackageSide,
  PackageRule,
  ruleAmountLabel,
  snapshotValue,
  TotalsSnapshot,
} from './packaging-preview.util';

@Component({
  selector: 'app-packaging-preview',
  templateUrl: './packaging-preview.component.html',
  styleUrls: ['./packaging-preview.component.scss'],
  imports: [
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCheckbox,
    MatIcon,
    FormsModule,
    DecimalPipe,
    PackageSelectorNamePipe,
  ],
})
export class PackagingPreviewComponent implements OnChanges {
  private readonly cc = inject(CalcConfigService);
  private readonly institutionalMethodFromService = toSignal(this.cc.institutionalMethod$);

  @Input() published: PackagePreviewRow[] = [];
  @Input() draft: PackagePreviewRow[] = [];
  /** Optional override; defaults to CalcConfigService.institutionalMethod$. */
  @Input() institutionalMethod?: boolean;

  readonly sides = PACKAGE_SIDES;

  packages: PackageCompare[] = [];
  differencesOnly = false;
  showAllRules = false;
  showMethodologyNeed = false;

  readonly formatDelta = formatDelta;
  readonly metricChanged = metricChanged;
  readonly metricDelta = metricDelta;
  readonly ruleAmountLabel = ruleAmountLabel;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['published'] && !changes['draft']) {
      return;
    }
    const expanded = new Map(this.packages.map(pkg => [pkg.packId, pkg.expanded]));
    this.packages = buildPackagingCompare(this.published ?? [], this.draft ?? [], expanded);
    this.syncShowAllRules();
  }

  get methodologyKey() {
    return methodologyNeedKey(this.resolvedInstitutionalMethod());
  }

  get showMethodologyToggle(): boolean {
    return this.methodologyKey !== null;
  }

  get methodologyToggleLabel(): string {
    return this.methodologyKey === 'fmNeed'
      ? 'Show FM remaining need'
      : 'Show IM remaining need';
  }

  get visiblePackages(): PackageCompare[] {
    return this.differencesOnly
      ? this.packages.filter(pkg => pkg.hasDifference)
      : this.packages;
  }

  get differenceCount(): number {
    return this.packages.filter(pkg => pkg.hasDifference).length;
  }

  get sections() {
    const packages = this.visiblePackages;
    const sections = [];

    const contributions = packages.filter(pkg => pkg.isContribution);
    if (contributions.length > 0) {
      sections.push({
        id: 'contribution' as const,
        title: 'Contributions',
        note: 'Adjusts adjusted remaining need only. Remaining COA is not affected.',
        packages: contributions,
      });
    }

    const aid = packages.filter(pkg => !pkg.isContribution);
    if (aid.length > 0) {
      sections.push({
        id: 'aid' as const,
        title: 'Aid',
        packages: aid,
      });
    }

    return sections;
  }

  columnsFor(sectionId: 'contribution' | 'aid') {
    return ledgerColumns(sectionId, this.showMethodologyNeed, this.methodologyKey);
  }

  sectionStarting(sectionId: 'contribution' | 'aid', side: PackageSide): TotalsSnapshot | null {
    const list = sectionId === 'contribution'
      ? this.visiblePackages.filter(pkg => pkg.isContribution)
      : this.visiblePackages.filter(pkg => !pkg.isContribution);
    return list[0]?.[side]?.before ?? null;
  }

  startingValue(starting: TotalsSnapshot, columnId: string): number | null {
    return snapshotValue(starting, columnId);
  }

  columnMetricKey(columnId: string): 'adjNeed' | 'remainingCoa' | 'fmNeed' | 'imNeed' {
    return columnId as 'adjNeed' | 'remainingCoa' | 'fmNeed' | 'imNeed';
  }

  setAllRulesExpanded(expanded: boolean): void {
    this.showAllRules = expanded;
    for (const pkg of this.visiblePackages) {
      pkg.expanded = expanded;
    }
  }

  toggleExpanded(pkg: PackageCompare): void {
    pkg.expanded = !pkg.expanded;
    this.syncShowAllRules();
  }

  onFilterChange(): void {
    this.syncShowAllRules();
  }

  stepFor(pkg: PackageCompare, side: PackageSide) {
    return pkg[side];
  }

  ruleInputUsesPipe(rule: PackageRule): boolean {
    return rule.assignType !== 'Fixed' && rule.input.includes('.');
  }

  private syncShowAllRules(): void {
    const visible = this.visiblePackages;
    this.showAllRules = visible.length > 0 && visible.every(pkg => pkg.expanded);
  }

  private resolvedInstitutionalMethod(): boolean | null | undefined {
    return this.institutionalMethod ?? this.institutionalMethodFromService();
  }
}
